/**
 * Display Rule Matcher
 *
 * Walks a rule list in order and returns the first rule that matches the context,
 * along with the delay that rule asks for. The rule itself comes back rather than a
 * bare "matched" so the caller can read what else it carries -- the display choice,
 * today -- without re-running the walk to find which one it was.
 */

import type { DisplayRule } from '@/domain/configuration';
import { DISPLAY_RULE_TYPE } from '@/domain/configuration';
import urlDisplayRuleMatcher from '@/lib/displayRules/urlDisplayRuleMatcher';

interface MatchContext {
  url: string;
}

interface MatchResult<T> {
  displayDelay: number;
  /** The first rule that matched, or null when none did. */
  matched: T | null;
}

function getRuleEvaluationMap(): Record<string, (rule: DisplayRule, context: MatchContext) => boolean> {
  const map: Record<string, (rule: DisplayRule, context: MatchContext) => boolean> = {};
  map[DISPLAY_RULE_TYPE.URL] = urlDisplayRuleMatcher;
  return map;
}

const ruleEvaluationMap = getRuleEvaluationMap();

// Generic over the caller's own rule shape: the matcher reads only the DisplayRule
// fields, and hands back whatever richer rule it was given.
export default function displayRuleMatcher<T extends DisplayRule>(
  rules: T[] = [],
  context: MatchContext = { url: '' },
): MatchResult<T> {
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const ruleEvaluator = ruleEvaluationMap[rule.type];
    if (!ruleEvaluator) {
      continue;
    }
    if (ruleEvaluator(rule, context)) {
      return {
        displayDelay: rule.displayDelay ? rule.displayDelay : 0,
        matched: rule,
      };
    }
  }
  return {
    displayDelay: 0,
    matched: null,
  };
}
